import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import toast from "react-hot-toast";
import InputField from "components/fields/InputField";
import { useGlobalContext } from "context/globalContext";
import { getAccessTokenAPI } from "Api/getAccessToken";
import { generateOtpAPI, verifyOtpAPI, adminLoginAPI } from "Api/emailAPI";
import Checkbox from "components/checkbox";
import { IoMdEye, IoMdEyeOff } from "react-icons/io";
import ForgetPasswordDialog from "../../components/forgotpassword/ForgetPasswordDialog";
import { decryptData } from "helpers";

export default function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOTP] = useState("");
  const [userId, setUserId] = useState("");
  const [showOTPField, setShowOTPField] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [otpTimerExpired, setOtpTimerExpired] = useState(false);
  const [timer, setTimer] = useState(60);
  const navigate = useNavigate();
  const { setUserDetails, accessJWT, setAccessJWT } = useGlobalContext();
  const [openForgotDialog, setOpenForgotDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    async function getAccessToken() {
      let success = false;
      let count = 0;
      while (!success && count <= 4) {
        try {
          const response = await getAccessTokenAPI();
          if (response?.statusCode === 200 && response?.data) {
            setAccessJWT(response?.data);
            success = true;
          }
        } catch (err) {
          console.error("Error fetching access token:", err);
        }
        count++;
      }
    }
    if (!accessJWT) getAccessToken();
  }, [accessJWT, setAccessJWT]);

  const startOtpTimer = () => {
    setOtpTimerExpired(false);
    setTimer(60);
    const interval = setInterval(() => {
      setTimer((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(interval);
          setOtpTimerExpired(true);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
  };

  const handleLogin = async () => {
    setError("");
    if (!email || !password) {
      setError("Please enter both email and password.");
      return;
    }

    try {
      setLoading(true);
      const loginRes = await adminLoginAPI({ email, password }, accessJWT);

      if (loginRes?.statusCode === 200 && loginRes?.data) {
        const token = loginRes.data?.jwtToken;
        const decodedToken = decryptData(token);
        const decoded = jwtDecode(decodedToken);

        const uid = decoded?.id;

        if (!uid) {
          setError("User ID not found in token.");
          return;
        }
        
        if (rememberMe) {
          localStorage.setItem("rememberedEmail", email);
          localStorage.setItem("rememberedPassword", password);
        } else {
          localStorage.removeItem("rememberedEmail");
          localStorage.removeItem("rememberedPassword");
        }

        setUserId(uid);
        localStorage.setItem("userId", uid);
        setShowOTPField(true);
        startOtpTimer();
        toast.success("OTP sent successfully!");
      } else {
        setError("Invalid email or password.");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    const rememberedPassword = localStorage.getItem("rememberedPassword");

    if (rememberedEmail && rememberedPassword) {
      setEmail(rememberedEmail);
      setPassword(rememberedPassword);
      setRememberMe(true);
    }
  }, []);

  const handleGenerateNewOTP = async () => {
    setError("");
    setOTP("");
    try {
      setLoading(true);
      const otpRes = await generateOtpAPI({ email, userId }, accessJWT);

      if (otpRes?.statusCode === 200) {
        startOtpTimer();
        toast.success("OTP Regenerated.");
      } else {
        setError(otpRes?.message || "Failed to send OTP.");
      }
    } catch (err) {
      console.error("OTP generation error:", err);
      setError("Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError("");

    if (!otp) {
      setError("Please enter the OTP.");
      return;
    }

    if (!userId) {
      setError("User ID missing. Please log in again.");
      return;
    }

    try {
      setLoading(true);
      const storedUserId = localStorage.getItem("userId");

      const response = await verifyOtpAPI(
        { userId: storedUserId, otp },
        accessJWT
      );

      if (response?.statusCode === 200 && response?.data?.jwtToken) {
        const { jwtToken } = response.data;
        localStorage.setItem("jwtToken", jwtToken);
        const decodedToken = jwtDecode(decryptData(jwtToken));

        localStorage.setItem("userDetails", JSON.stringify(decodedToken));

        const allowedRoles = [1, 2, 3, 4];
        if (!allowedRoles.includes(decodedToken?.role)) {
          navigate("/auth/login");
          localStorage.removeItem("jwtToken");
          setUserDetails({});
          toast.error("Invalid login credentials");
          return;
        }

        setUserDetails(decodedToken);
        toast.success("Login successful!");

      
        navigate("/admin/admin-prompt");
      } else {
        setError(response?.message || "OTP verification failed");
      }
    } catch (err) {
      console.error("OTP verification error:", err);
      setError("OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!showOTPField) {
      await handleLogin();
    } else {
      await handleVerifyOtp();
    }
  };

  const renderSubmitButtonText = () => {
    if (loading) return "Processing...";
    return showOTPField ? "Verify OTP" : "Log in";
  };

  return (
    <div className="mt-16 mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-center">
      <div className="mt-[10vh] w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]">
        <h4 className="mb-2.5 text-4xl font-bold text-navy-700 dark:text-white">
          Sign In
        </h4>
        <p className="mb-9 ml-1 text-base text-gray-600">
          {showOTPField
            ? "Enter the OTP sent to your email to verify user!"
            : "Enter your email and password to sign in!"}
        </p>

        {error && <p className="mb-4 text-red-500">{error}</p>}

        <form onSubmit={handleSignIn}>
          {!showOTPField && (
            <>
              <InputField
                variant="auth"
                extra="mb-3"
                label="Email*"
                placeholder="mail@simmmple.com"
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <div
                className="relative"
                style={{ position: "relative", zIndex: 10 }}
              >
                <InputField
                  variant="auth"
                  extra="mb-3"
                  label="Password*"
                  placeholder="Min. 8 characters"
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <div
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-4 top-[52px] cursor-pointer text-gray-500 dark:text-white"
                >
                  {showPassword ? (
                    <IoMdEye size={20} className=" dark:text-gray-10 " />
                  ) : (
                    <IoMdEyeOff size={20} className=" dark:text-gray-10 " />
                  )}
                </div>
              </div>
            </>
          )}

          {showOTPField && (
            <>
              <InputField
                variant="auth"
                extra="mb-3"
                label="Enter OTP*"
                placeholder="6-digit code"
                id="otp"
                type="text"
                maxLength={6}
                value={otp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  if (value.length <= 6) {
                    setOTP(value);
                  }
                }}
                required
              />
              {!otpTimerExpired && (
                <p className="mt-2 text-sm text-gray-500">
                  You can request a new OTP in{" "}
                  <span className="font-semibold"> {timer} seconds🕙</span>{" "}
                </p>
              )}
            </>
          )}

          {!showOTPField && (
            <div className="mb-4 flex items-center justify-between px-2">
              <div className="flex items-center">
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <p className="ml-2 text-sm font-medium text-navy-700 dark:text-white">
                  Remember me
                </p>
              </div>

              <button
                type="button"
                style={{ position: "relative", zIndex: 10 }}
                className="text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-white"
                onClick={() => setOpenForgotDialog(true)}
              >
                Forgot Password?
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || (showOTPField && otpTimerExpired)}
            className={`linear mt-2 w-full rounded-xl py-[12px] text-base font-medium text-white transition duration-200 ${
              loading || otpTimerExpired
                ? "cursor-not-allowed bg-gray-400"
                : "bg-brand-500 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
            }`}
            style={{ position: "relative", zIndex: 20, pointerEvents: "auto" }}
          >
            {renderSubmitButtonText()}
          </button>
        </form>

        {otpTimerExpired && (
          <button
            onClick={handleGenerateNewOTP}
            className="mt-4 text-sm text-brand-500 hover:text-brand-600 dark:text-white"
            disabled={loading}
          >
            Resend OTP
          </button>
        )}
        {openForgotDialog && (
          <ForgetPasswordDialog onClose={() => setOpenForgotDialog(false)} />
        )}
      </div>
    </div>
  );
}